import { RootState } from '../../../../../init-store';
import { SELF } from '../../../../shared/constants';
import { isMessengerIntegration } from '../../../../shared/features/config/selectors';
import { PersonalPermission, Role, RolePermission } from '../../../../shared/helpers/permissions';
import { getParticipantById } from '../../selectors';
import { getParticipantRole } from './helpers';

const getPermissions = (state: RootState) => state.call.permissions;

export const getPermissionsVersion = (state: RootState): Maybe<number> => state.call.permissions?.version;

/**
 * Доступна ли "Ролевая модель"
 */
export const isRoleModelEnabledSelector = (state: RootState): boolean =>
    !isMessengerIntegration(state) && Boolean(getPermissions(state));

/**
 * Получить роль текущего пользователя
 */
export const getSelfRole = (state: RootState): Role => {
    const participant = getParticipantById(state, SELF);
    return getParticipantRole(participant);
};

const hasSelfRolePermission = (state: RootState, permission: RolePermission): boolean => {
    const permissionsState = getPermissions(state);

    if (!permissionsState) {
        return false;
    }

    const selfRole = getSelfRole(state);
    const permissionsRoles = permissionsState.publicRolePermissions.map((permissionInfo) => permissionInfo.role);

    // Если не указаны права для огранизатора встречи, то смотрим на права соорганизаторов
    const role = selfRole === Role.OWNER && !permissionsRoles.includes(selfRole) ? Role.ADMIN : selfRole;

    return permissionsState.publicRolePermissions.some(
        (rolePermissionState) => rolePermissionState.role === role && rolePermissionState.allowed.includes(permission)
    );
};

const hasSelfPersonalPermission = (state: RootState, permission: PersonalPermission): boolean => {
    const permissionsState = getPermissions(state);

    if (!permissionsState) {
        return false;
    }

    return permissionsState.personalAllowed.includes(permission);
};

export const hasRolePermission = (state: RootState, permission: RolePermission, role: Role): boolean => {
    const permissionsState = getPermissions(state);

    if (!permissionsState) {
        return false;
    }

    return permissionsState.publicRolePermissions.some(
        (rolePermissionState) => rolePermissionState.role === role && rolePermissionState.allowed.includes(permission)
    );
};

const hasMemberRolePermission = (state: RootState, permission: RolePermission) =>
    hasRolePermission(state, permission, Role.MEMBER);

const hasAdminRolePermission = (state: RootState, permission: RolePermission) =>
    hasRolePermission(state, permission, Role.ADMIN);

const hasOwnerRolePermission = (state: RootState, permission: RolePermission) =>
    hasRolePermission(state, permission, Role.OWNER);

/**
 * Может ли пользователь закрыть комнату для всех
 */
export const isCloseRoomAllowed = (state: RootState) => {
    return hasSelfRolePermission(state, RolePermission.CLOSE_ROOM);
};

/**
 * Может ли пользователь вести запись локально
 */
export const isRecordingAllowedSelector = (state: RootState) => {
    return !isRoleModelEnabledSelector(state) || hasSelfRolePermission(state, RolePermission.RECORDING);
};

export const selectHasRecordingMutePermission = (state: RootState) =>
    hasSelfRolePermission(state, RolePermission.RECORDING_MUTE) ||
    hasSelfPersonalPermission(state, PersonalPermission.RECORDING_MUTE);

/**
 * Может ли пользователь запрещать запись остальным участникам
 */
export const isRecordingBanAllowedSelector = (state: RootState) => {
    return hasSelfPersonalPermission(state, PersonalPermission.RECORDING_BAN_ROLE);
};

/**
 * Может ли пользователь управлять правами пользователей
 */
export const isPermissionsChangeAllowedSelector = (state: RootState) => {
    return (
        isRoleModelEnabledSelector(state) &&
        (isRecordingBanAllowedSelector(state) ||
            selectHasCloudRecordingBanRolePermission(state) ||
            selectHasSummarizationBanRolePermission(state))
    );
};

/**
 * Получить все права обычных(роль MEMBER) участников
 */
export const getMembersPermissions = (state: RootState) => {
    const permissionsState = getPermissions(state);

    return permissionsState?.publicRolePermissions.find(
        (rolePermissionState) => rolePermissionState.role === Role.MEMBER
    )?.allowed;
};

/**
 * Разрешена ли локальная запись для обычных(роль MEMBER) участников
 */
export const isRecordingForMembersAllowedSelector = (state: RootState) => {
    return hasMemberRolePermission(state, RolePermission.RECORDING);
};

/**
 * Может ли пользователь вести облачную запись
 */
export const selectHasCloudRecordingPermission = (state: RootState) =>
    hasSelfRolePermission(state, RolePermission.CLOUD_RECORDING);

/**
 * Может ли пользователь запрещать облачную запись остальным участникам
 */
export const selectHasCloudRecordingBanRolePermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.CLOUD_RECORDING_BAN_ROLE);

/**
 * Разрешена ли облачная запись для обычных(роль MEMBER) участников
 */
export const selectHasMembersCloudRecordingPermission = (state: RootState) =>
    hasMemberRolePermission(state, RolePermission.CLOUD_RECORDING);

/**
 * Может ли пользователь вести саммаризацию
 */
export const selectHasSummarizationPermission = (state: RootState) => {
    const participant = getParticipantById(state, SELF);

    return (
        hasSelfRolePermission(state, RolePermission.SUMMARIZATION) &&
        (participant?.publicPermissionsOverride?.summarization === undefined ||
            Boolean(participant.publicPermissionsOverride.summarization))
    );
};

/**
 * Может ли пользователь запрещать саммаризацию остальным участникам
 */
export const selectHasSummarizationBanRolePermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.SUMMARIZATION_BAN_ROLE);

/**
 * Может ли пользователь изменять настройки саммаризации (получателя рассылки, к примеру)
 */
export const selectHasSummarizationSettingsPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.SUMMARIZATION_SETTINGS);

/**
 * Разрешена ли саммаризация для обычных(роль MEMBER) участников
 */
export const selectHasMembersSummarizationPermission = (state: RootState) =>
    hasMemberRolePermission(state, RolePermission.SUMMARIZATION);

/**
 * Может ли пользователь отправлять реакции
 */
export const selectHasReactionsPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.REACTION);

/**
 * Включена ли рассылка саммаризации присутствовавшим на конфе обычным участникам (роль MEMBER)
 */
export const selectHasMembersSummarizationReceivePermission = (state: RootState) =>
    hasMemberRolePermission(state, RolePermission.SUMMARIZATION_RECEIVE);

/**
 * Включена ли рассылка саммаризации присутствовавшим на конфе сооргам
 */
export const selectHasAdminsSummarizationReceivePermission = (state: RootState) =>
    hasAdminRolePermission(state, RolePermission.SUMMARIZATION_RECEIVE);

/**
 * Включена ли рассылка саммаризации присутствовавшему на конфе оргу
 */
export const selectHasOwnerSummarizationReceivePermission = (state: RootState) =>
    hasOwnerRolePermission(state, RolePermission.SUMMARIZATION_RECEIVE);

/**
 * Включена ли рассылка саммаризации приглашенным в календаре обычным участникам (роль MEMBER)
 */
export const selectHasMembersCalendarSummarizationReceivePermission = (state: RootState) =>
    hasMemberRolePermission(state, RolePermission.CALENDAR_SUMMARIZATION_RECEIVE);

/**
 * Включена ли рассылка саммаризации приглашенным в календаре сооргам
 */
export const selectHasAdminsCalendarSummarizationReceivePermission = (state: RootState) =>
    hasAdminRolePermission(state, RolePermission.CALENDAR_SUMMARIZATION_RECEIVE);

/**
 * Включена ли рассылка саммаризации приглашенному в календаре оргу
 */
export const selectHasOwnerCalendarSummarizationReceivePermission = (state: RootState) =>
    hasOwnerRolePermission(state, RolePermission.CALENDAR_SUMMARIZATION_RECEIVE);

export const selectHasDesktopPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.DESKTOP) ||
    hasSelfRolePermission(state, RolePermission.DESKTOP);

export const selectHasDesktopMutePermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.DESKTOP_MUTE) ||
    hasSelfRolePermission(state, RolePermission.DESKTOP_MUTE);

export const selectHasCameraPermission = (state: RootState) =>
    getPermissionsVersion(state) === undefined || hasSelfRolePermission(state, RolePermission.CAMERA);

export const selectHasCameraMutePermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.CAMERA_MUTE) ||
    hasSelfRolePermission(state, RolePermission.CAMERA_MUTE);

export const selectHasMicrophonePermission = (state: RootState) =>
    getPermissionsVersion(state) === undefined || hasSelfRolePermission(state, RolePermission.MICROPHONE);

export const selectHasMicrophoneMutePermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.MICROPHONE_MUTE) ||
    hasSelfRolePermission(state, RolePermission.MICROPHONE_MUTE);

export const selectHasAccessLevelEditPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.ACCESS_LEVEL_EDIT) ||
    hasSelfRolePermission(state, RolePermission.ACCESS_LEVEL_EDIT);

export const selectHasAdmitPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.ADMIT) || hasSelfRolePermission(state, RolePermission.ADMIT);

export const selectHasAdmitAllPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.ADMIT_ALL) ||
    hasSelfRolePermission(state, RolePermission.ADMIT_ALL);

export const selectHasCloseRoomPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.CLOSE_ROOM) ||
    hasSelfRolePermission(state, RolePermission.CLOSE_ROOM);

/**
 * Может ли пользователь редактировать матрицу пермиссий
 */
export const selectHasConferencePermissionsEditMatrixPermission = (state: RootState) =>
    hasSelfPersonalPermission(state, PersonalPermission.CONFERENCE_PERMISSIONS_EDIT_MATRIX) ||
    hasSelfRolePermission(state, RolePermission.CONFERENCE_PERMISSIONS_EDIT_MATRIX);
